import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Phone, MapPin, DollarSign, Pencil, Save, X, FileText, User, Calendar, Hash, Building, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { MailingHistoryTimeline } from "./MailingHistoryTimeline";

interface MailingHistory {
  id: string;
  campaign_name: string | null;
  mailed_date: string | null;
  mailing_round: number;
  letter_type: string | null;
  returned: boolean;
  response_received: boolean;
  response_date: string | null;
  response_notes: string | null;
}

export function MailingRecordDetail({ record, onClose, onUpdated, onNavigate, currentIndex, totalCount }: { record: any; onClose: () => void; onUpdated?: () => void; onNavigate?: (direction: "prev" | "next") => void; currentIndex?: number; totalCount?: number }) {
  const [history, setHistory] = useState<MailingHistory[]>([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: record.first_name || "",
    last_name: record.last_name || "",
    suffix: record.suffix || "",
    business_name: record.business_name || "",
    street_address: record.street_address || "",
    city: record.city || "",
    state: record.state || "",
    zip_code: record.zip_code || "",
    county: record.county || "",
    origin_state: record.origin_state || "",
    property_address: record.property_address || "",
    phone_number: record.phone_number || "",
    email_address: record.email_address || "",
    document_type: record.document_type || "",
    response: record.response || "",
    mailing_status: record.mailing_status || "active",
    tm_notes: record.tm_notes || "",
    tm_dropdown: record.tm_dropdown || "",
  });

  useEffect(() => {
    fetchHistory();
    setForm({
      first_name: record.first_name || "",
      last_name: record.last_name || "",
      suffix: record.suffix || "",
      business_name: record.business_name || "",
      street_address: record.street_address || "",
      city: record.city || "",
      state: record.state || "",
      zip_code: record.zip_code || "",
      county: record.county || "",
      origin_state: record.origin_state || "",
      property_address: record.property_address || "",
      phone_number: record.phone_number || "",
      email_address: record.email_address || "",
      document_type: record.document_type || "",
      response: record.response || "",
      mailing_status: record.mailing_status || "active",
      tm_notes: record.tm_notes || "",
      tm_dropdown: record.tm_dropdown || "",
    });
    setEditing(false);
  }, [record.id]);

  const fetchHistory = async () => {
    const { data } = await supabase
      .from("mailing_history")
      .select("*")
      .eq("mailing_record_id", record.id)
      .order("mailing_round", { ascending: true });
    setHistory(data || []);
  };

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("mailing_records")
      .update({
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        suffix: form.suffix || null,
        business_name: form.business_name || null,
        street_address: form.street_address || null,
        city: form.city || null,
        state: form.state || null,
        zip_code: form.zip_code || null,
        county: form.county || null,
        origin_state: form.origin_state || null,
        property_address: form.property_address || null,
        phone_number: form.phone_number || null,
        email_address: form.email_address || null,
        document_type: form.document_type || null,
        response: form.response || null,
        mailing_status: form.response ? "responded" : form.mailing_status,
        tm_notes: form.tm_notes || null,
        tm_dropdown: form.tm_dropdown || null,
      })
      .eq("id", record.id);

    setSaving(false);
    if (error) {
      toast.error("Failed to save");
    } else {
      toast.success("Record updated");
      setEditing(false);
      onUpdated?.();
    }
  };

  const update = (field: string, value: string) => setForm(f => ({ ...f, [field]: value }));

  const renderField = (label: string, field: string, type = "text", colSpan = false) => (
    <div className={colSpan ? "col-span-2" : ""} key={field}>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      {editing ? (
        type === "textarea" ? (
          <Textarea value={(form as any)[field]} onChange={e => update(field, e.target.value)} className="text-sm min-h-[60px]" />
        ) : (
          <Input value={(form as any)[field]} onChange={e => update(field, e.target.value)} className="text-sm h-8" />
        )
      ) : (
        <p className="text-sm font-medium">{(form as any)[field] || "—"}</p>
      )}
    </div>
  );

  const renderReadOnly = (label: string, value: any, icon?: React.ReactNode) => (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-sm font-medium flex items-center gap-1.5">
        {icon}
        {value || "—"}
      </p>
    </div>
  );

  const displayName = [record.first_name, record.last_name, record.suffix].filter(Boolean).join(" ") || record.business_name || "Unknown";

  return (
    <div className="space-y-5">
      {/* Navigation Bar */}
      {onNavigate && currentIndex !== undefined && totalCount !== undefined && (
        <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2">
          <Button variant="outline" size="sm" disabled={currentIndex <= 0} onClick={() => onNavigate("prev")}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <span className="text-xs text-muted-foreground font-medium">
            {currentIndex + 1} of {totalCount}
          </span>
          <Button variant="outline" size="sm" disabled={currentIndex >= totalCount - 1} onClick={() => onNavigate("next")}>
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold">{displayName}</h3>
          <p className="text-xs text-muted-foreground font-mono">ID: {record.seller_id}</p>
        </div>
        <div className="flex gap-2 items-center">
          <Badge variant={form.mailing_status === "active" ? "default" : form.mailing_status === "do_not_mail" ? "destructive" : form.mailing_status === "temporarily_away" ? "outline" : "secondary"}>
            {form.mailing_status === "temporarily_away" ? "Temporarily Away" : form.mailing_status}
          </Badge>
          {editing ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save"}
              </Button>
            </>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4 mr-1" /> Edit
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10">
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this record?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete <strong>{displayName}</strong> and all associated mailing history. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={async () => {
                    await supabase.from("mailing_history").delete().eq("mailing_record_id", record.id);
                    const { error } = await supabase.from("mailing_records").delete().eq("id", record.id);
                    if (error) { toast.error("Failed to delete record"); return; }
                    toast.success("Record deleted");
                    onUpdated?.();
                    onClose();
                  }}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* ===== CALL PREP: Phone & Contact (top priority) ===== */}
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
        <p className="text-sm font-semibold flex items-center gap-1.5 text-primary">
          <Phone className="h-4 w-4" /> Call Prep — Contact Info
        </p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Phone Number</p>
            {editing ? (
              <Input value={form.phone_number} onChange={e => update("phone_number", e.target.value)} className="text-sm h-8" />
            ) : form.phone_number ? (
              <Button
                variant="outline"
                size="sm"
                className="font-mono text-sm"
                onClick={() => {
                  navigator.clipboard.writeText(form.phone_number);
                  toast.success("Phone number copied!");
                }}
              >
                <Phone className="h-3 w-3 mr-1.5" />
                {form.phone_number}
              </Button>
            ) : (
              <p className="text-sm font-medium">—</p>
            )}
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Call Result</p>
            <Select value={form.tm_dropdown} onValueChange={v => {
              update("tm_dropdown", v);
              // Auto-save call result immediately
              supabase.from("mailing_records").update({ tm_dropdown: v }).eq("id", record.id).then(({ error }) => {
                if (error) toast.error("Failed to save call result");
                else { toast.success("Call result saved"); onUpdated?.(); }
              });
            }}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select result..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="left_message">Left Message</SelectItem>
                <SelectItem value="didnt_leave_message">Didn't Leave a Message</SelectItem>
                <SelectItem value="busy">Busy</SelectItem>
                <SelectItem value="no_answer">No Answer</SelectItem>
                <SelectItem value="bad_number">Bad Number</SelectItem>
                <SelectItem value="good_call">Good Call - See Notes</SelectItem>
                <SelectItem value="callback">Callback Requested</SelectItem>
                <SelectItem value="not_interested">Not Interested</SelectItem>
                <SelectItem value="do_not_call">Do Not Call</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {renderField("Email Address", "email_address")}
        </div>
        {/* Melissa Demographics */}
        {(record.date_of_birth || record.household_income || record.credit_card_user) && (
          <div className="grid grid-cols-3 gap-3 pt-2 border-t border-primary/20">
            {renderReadOnly("Age / DOB", record.date_of_birth ? (() => {
              const yr = parseInt(record.date_of_birth.substring(0, 4));
              const mo = parseInt(record.date_of_birth.substring(4, 6)) - 1;
              const age = Math.floor((Date.now() - new Date(yr, mo, 1).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
              return `${age} yrs (${record.date_of_birth.substring(4, 6)}/${record.date_of_birth.substring(0, 4)})`;
            })() : null)}
            {renderReadOnly("Household Income", record.household_income)}
            {renderReadOnly("Credit Card User", record.credit_card_user)}
          </div>
        )}
        <div>
          <p className="text-xs text-muted-foreground mb-1">Quick Notes</p>
          <Textarea
            value={form.tm_notes}
            onChange={e => update("tm_notes", e.target.value)}
            onBlur={() => {
              supabase.from("mailing_records").update({ tm_notes: form.tm_notes || null }).eq("id", record.id).then(({ error }) => {
                if (error) toast.error("Failed to save notes");
                else { toast.success("Notes saved"); onUpdated?.(); }
              });
            }}
            placeholder="Type call notes here..."
            className="text-sm min-h-[50px]"
          />
        </div>
      </div>

      {/* ===== CONTRACT / NOTE DETAILS ===== */}
      <div className="rounded-lg border p-4 space-y-3">
        <p className="text-sm font-semibold flex items-center gap-1.5">
          <FileText className="h-4 w-4" /> Contract / Note Details
        </p>
        <div className="grid grid-cols-3 gap-3">
          {renderReadOnly("Document Type", record.document_type, <FileText className="h-3 w-3 text-muted-foreground" />)}
          {renderReadOnly("Recorded Date", record.recorded_date)}
          {renderReadOnly("Consideration", record.consideration ? `$${Number(record.consideration).toLocaleString()}` : null, <DollarSign className="h-3 w-3 text-muted-foreground" />)}
          {renderReadOnly("County", record.county)}
          {renderReadOnly("Origin State", record.origin_state)}
          {renderReadOnly("PID / Parcel", record.pid, <Hash className="h-3 w-3 text-muted-foreground" />)}
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Property Address</p>
          <p className="text-sm font-medium">{record.property_address || "—"}</p>
        </div>
      </div>

      {/* ===== SELLER / CONTACT IDENTITY ===== */}
      <div className="rounded-lg border p-4 space-y-3">
        <p className="text-sm font-semibold flex items-center gap-1.5">
          <User className="h-4 w-4" /> Seller Identity
        </p>
        <div className="grid grid-cols-2 gap-3">
          {renderField("First Name", "first_name")}
          {renderField("Last Name", "last_name")}
          {renderField("Suffix", "suffix")}
          {renderField("Business Name", "business_name")}
        </div>
      </div>

      {/* ===== MAILING ADDRESS ===== */}
      <div className="rounded-lg border p-4 space-y-3">
        <p className="text-sm font-semibold flex items-center gap-1.5">
          <MapPin className="h-4 w-4" /> Mailing Address
        </p>
        <div className="grid grid-cols-2 gap-3">
          {renderField("Street Address", "street_address", "text", true)}
          {renderField("City", "city")}
          {renderField("State", "state")}
          {renderField("Zip Code", "zip_code")}
          {renderField("County", "county")}
          {renderField("Origin State", "origin_state")}
        </div>
      </div>

      {/* ===== MAILING & RESPONSE STATS ===== */}
      <div className="rounded-lg border p-4 space-y-3">
        <p className="text-sm font-semibold flex items-center gap-1.5">
          <Calendar className="h-4 w-4" /> Mailing & Response History
        </p>
        <div className="grid grid-cols-4 gap-3">
          {renderReadOnly("Times Mailed", record.times_mailed)}
          {renderReadOnly("Last Mailed", record.last_mailed_date || "Never")}
          {renderReadOnly("Response", record.response || "None")}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Status</p>
            {editing ? (
              <Select value={form.mailing_status} onValueChange={v => update("mailing_status", v)}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="responded">Responded</SelectItem>
                  <SelectItem value="temporarily_away">Temporarily Away</SelectItem>
                  <SelectItem value="do_not_mail">Do Not Mail</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Badge variant={form.mailing_status === "active" ? "default" : form.mailing_status === "do_not_mail" ? "destructive" : form.mailing_status === "temporarily_away" ? "outline" : "secondary"}>
                {form.mailing_status === "temporarily_away" ? "Temporarily Away" : form.mailing_status}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* ===== TELEMARKETING NOTES ===== */}
      <div className="rounded-lg border p-4 space-y-3">
        <p className="text-sm font-semibold flex items-center gap-1.5">
          <Phone className="h-4 w-4" /> Telemarketing Notes
        </p>
        <div className="grid grid-cols-2 gap-3">
          {renderField("TM Dropdown", "tm_dropdown")}
          {renderField("Response", "response")}
          {renderField("TM Notes", "tm_notes", "textarea", true)}
        </div>
      </div>

      <Separator />

      {/* Mailing History Timeline */}
      <MailingHistoryTimeline history={history} onResponseSaved={async () => {
        await fetchHistory();
        // Re-read the record's current status from DB so badge updates
        const { data } = await supabase.from("mailing_records").select("mailing_status, response").eq("id", record.id).single();
        if (data) {
          setForm(f => ({ ...f, mailing_status: data.mailing_status, response: data.response || "" }));
        }
        onUpdated?.();
      }} recordId={record.id} />
    </div>
  );
}
